import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, GraduationCap, Users2, Boxes, Wallet, UserSquare2, ChevronRight, X } from "lucide-react";
import api from "@/lib/api";
import { useDebounce } from "@/lib/useDebounce";

const SECTION_META = {
  students: { label: "Students", icon: GraduationCap, tint: "text-[#9DCD3F]" },
  staff:    { label: "Staff",    icon: Users2,        tint: "text-[#1B2C5A]" },
  assets:   { label: "Assets",   icon: Boxes,         tint: "text-amber-600" },
  fees:     { label: "Fees",     icon: Wallet,        tint: "text-emerald-600" },
  visitors: { label: "Visitors", icon: UserSquare2,   tint: "text-pink-600" },
};

const GlobalSearch = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const dq = useDebounce(q, 200);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    const onKey = (e) => {
      // Ctrl/Cmd+K → focus
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        wrapRef.current?.querySelector("input")?.focus();
      }
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const term = dq.trim();
    if (!term) { setResults(null); return; }
    setLoading(true);
    api.get("/search/global", { params: { q: term } })
      .then(({ data }) => { if (!cancelled) setResults(data); })
      .catch(() => { if (!cancelled) setResults(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dq]);

  const goTo = (url) => { setOpen(false); setQ(""); navigate(url); };

  const sections = results ? Object.entries(results).filter(([, items]) => items.length > 0) : [];
  const total = results ? Object.values(results).reduce((a, items) => a + items.length, 0) : 0;
  const showDropdown = open && (loading || (dq.trim().length > 0));

  return (
    <div ref={wrapRef} className="relative flex-1 max-w-md" data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569] pointer-events-none" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search students, staff, assets, fees, visitors…"
          className="w-full h-10 pl-9 pr-20 rounded-md bg-[#FDFBF7] border border-[#E2E8F0] text-sm focus:outline-none focus:border-[#1B2C5A]"
          data-testid="topbar-search"
        />
        {q ? (
          <button type="button" onClick={() => { setQ(""); setResults(null); }} className="absolute right-12 top-1/2 -translate-y-1/2 text-[#475569] hover:text-[#1B2C5A]" aria-label="Clear search">
            <X className="w-3.5 h-3.5" />
          </button>
        ) : null}
        <kbd className="hidden md:inline-block absolute right-2 top-1/2 -translate-y-1/2 text-[10px] px-1.5 py-0.5 rounded border border-[#E2E8F0] bg-white text-[#475569]">⌘K</kbd>
      </div>

      {showDropdown && (
        <div className="absolute left-0 right-0 mt-2 rounded-lg border border-[#E2E8F0] bg-white shadow-xl z-50 max-h-[70vh] overflow-y-auto" data-testid="global-search-results">
          {loading && (
            <div className="px-4 py-6 text-sm text-[#475569] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Searching…</div>
          )}
          {!loading && results && total === 0 && (
            <div className="px-4 py-6 text-sm text-[#475569]">No matches for <strong className="text-[#1B2C5A]">"{dq}"</strong>.</div>
          )}
          {!loading && total > 0 && (
            <>
              <div className="px-3 py-2 text-[11px] overline text-[#9DCD3F] border-b border-[#E2E8F0]">{total} match{total !== 1 ? "es" : ""}</div>
              {sections.map(([key, items]) => {
                const meta = SECTION_META[key]; if (!meta) return null;
                const Icon = meta.icon;
                return (
                  <div key={key} className="border-b border-[#E2E8F0] last:border-b-0">
                    <div className="px-3 py-1.5 text-[11px] uppercase tracking-wider text-[#475569] bg-[#FDFBF7] flex items-center gap-1.5">
                      <Icon className={"w-3.5 h-3.5 " + meta.tint} /> {meta.label} ({items.length})
                    </div>
                    <ul>
                      {items.map((it) => (
                        <li key={it.id}>
                          <button
                            onClick={() => goTo(it.url)}
                            className="w-full text-left px-3 py-2 hover:bg-[#9DCD3F]/8 flex items-center gap-2 group"
                            data-testid={`gs-${key}-${it.id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-[#1B2C5A] truncate">{it.label}</div>
                              {it.sub && <div className="text-xs text-[#475569] truncate">{it.sub}</div>}
                            </div>
                            <ChevronRight className="w-3.5 h-3.5 text-[#475569] group-hover:text-[#1B2C5A]" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
